import { z } from 'zod';

import type { ToolDefinition } from '../types';

// Zod installs its English messages as a side effect of a module that only re-exports, and it
// declares itself side-effect free, so the production bundle drops that install and every issue
// reads a bare "Invalid input". The model fixes a rejected call from these messages.
z.config(z.locales.en());

type Params = ToolDefinition['function']['parameters'];

// zod registers its English messages as a side effect of its entry module, but the
// package declares `sideEffects: false`, so the production bundle drops that call and
// every issue falls back to a bare "Invalid input". Register the locale explicitly.
z.config(z.locales.en());

/**
 * Convert a Zod schema into the plain JSON Schema the LLM sees for a tool's
 * parameters. The emitted object is sent verbatim to the model (OpenAI Responses
 * `parameters` and Anthropic `input_schema`), so it must stay free of the
 * JSON-Schema authoring keywords (`$schema`, `$defs`/`definitions`, `$ref`) that
 * providers reject or that would change what the model sees. `reused: 'inline'`
 * keeps repeated sub-schemas inline rather than hoisting them into `$defs`.
 */
export function toLLMJSONSchema(schema: z.ZodType): Params {
    const json = z.toJSONSchema(schema, {
        target: 'draft-7',
        // Describe the schema as the model should PRODUCE it: optional fields stay
        // optional rather than being treated as required output.
        io: 'input',
        // Never hoist repeated sub-schemas into $defs/$ref — the model must see a
        // fully-inlined schema.
        reused: 'inline',
    });
    delete (json as { $schema?: unknown }).$schema;
    delete (json as { $defs?: unknown }).$defs;
    delete (json as { definitions?: unknown }).definitions;
    // Zod's integer type (`z.int()` / `z.number().int()`) bakes in the JS safe-integer
    // range as `minimum`/`maximum`. Hand-written tool schemas write a bare `type: 'integer'`,
    // so strip those sentinel bounds to stay wire-identical. Any real bound the author set
    // (e.g. `.min(0)`) is a different value and is kept.
    stripSafeIntegerBounds(json);
    // Zod emits a nullable field (`z.string().nullable()`) as `anyOf: [{type: 'string'},
    // {type: 'null'}]`, but hand-written tool schemas express the same thing as the compact
    // `type: ['string', 'null']`. Collapse the bare-scalar `anyOf` form back to a type array
    // so nullable fields stay wire-identical. Runs after `stripSafeIntegerBounds` so an
    // integer branch is already bare (`{type: 'integer'}`) and thus collapsible.
    collapseScalarAnyOf(json);
    // A plain `z.object` with no required fields makes `z.toJSONSchema` omit `required`
    // entirely, but hand-written tool schemas always include it (even empty). Restore the
    // convention so an all-optional schema-first tool stays wire-identical to its legacy form.
    if ((json as { required?: unknown }).required === undefined) {
        (json as { required: string[] }).required = [];
    }
    // `reused: 'inline'` inlines *repeated* sub-schemas, but a sub-schema carrying a
    // registry id (e.g. via `.meta({ id })`) is hoisted into `definitions` with a
    // `$ref` regardless. Having just dropped `definitions`, any surviving `$ref`
    // would dangle — a schema pointing at nothing, shipped to the model silently.
    // Fail loudly instead so the author inlines the sub-schema or drops its id.
    if (JSON.stringify(json).includes('"$ref":')) {
        throw new Error(
            'Tool schema emitted a $ref (a sub-schema with a registry id was hoisted); ' +
                'inline the sub-schema or remove its id so the model gets a self-contained schema.',
        );
    }
    // None of our three providers support `oneOf` — OpenAI, Anthropic, and Vertex
    // all accept `anyOf` and reject `oneOf`, so a schema carrying it 400s at the
    // provider. `z.discriminatedUnion` emits `oneOf` where `z.union` emits `anyOf`,
    // so fail here rather than in production.
    if (JSON.stringify(json).includes('"oneOf":')) {
        throw new Error(
            'Tool schema emitted a oneOf, which OpenAI, Anthropic, and Vertex all reject; ' +
                'use z.union (emits anyOf) instead of z.discriminatedUnion.',
        );
    }
    return json as Params;
}

/**
 * Recursively delete the JS safe-integer sentinel `minimum`/`maximum` that Zod emits for
 * integer schemas, so `type: 'integer'` ships bare (matching the hand-written schemas). A
 * real author-set bound has a different value and is left untouched.
 */
function stripSafeIntegerBounds(node: unknown): void {
    if (Array.isArray(node)) {
        node.forEach(stripSafeIntegerBounds);
        return;
    }
    if (!node || typeof node !== 'object') {
        return;
    }
    const obj = node as Record<string, unknown>;
    if (obj.type === 'integer') {
        if (obj.minimum === Number.MIN_SAFE_INTEGER) {
            delete obj.minimum;
        }
        if (obj.maximum === Number.MAX_SAFE_INTEGER) {
            delete obj.maximum;
        }
    }
    for (const value of Object.values(obj)) {
        stripSafeIntegerBounds(value);
    }
}

/**
 * Recursively collapse an `anyOf` whose every branch is a bare `{type: <scalar>}` into a
 * single `type: [<scalar>, ...]` on the parent — the form hand-written schemas use for
 * nullable fields (`{type: ['string', 'null']}`). A branch carrying anything beyond a
 * string-valued `type` (e.g. an object shape with `properties`) is a real union and is left
 * as `anyOf`, which the providers accept.
 */
function collapseScalarAnyOf(node: unknown): void {
    if (Array.isArray(node)) {
        node.forEach(collapseScalarAnyOf);
        return;
    }
    if (!node || typeof node !== 'object') {
        return;
    }
    const obj = node as Record<string, unknown>;
    const branches = obj.anyOf;
    if (
        Array.isArray(branches) &&
        branches.every((branch) => {
            if (!branch || typeof branch !== 'object') {
                return false;
            }
            const keys = Object.keys(branch as Record<string, unknown>);
            return (
                keys.length === 1 &&
                keys[0] === 'type' &&
                typeof (branch as { type: unknown }).type === 'string'
            );
        })
    ) {
        obj.type = branches.map((branch) => (branch as { type: string }).type);
        delete obj.anyOf;
    }
    for (const value of Object.values(obj)) {
        collapseScalarAnyOf(value);
    }
}

/**
 * The field a union uses to pick a branch, inferred from a failure. At most one
 * branch accepts the value supplied, so the discriminator is the field the other
 * branches all rejected — the most widely rejected field of all, since a branch
 * rejects the discriminator before anything else it wants. Counting per field
 * rather than per branch keeps this right when the intended branch has faults of
 * its own, and `invalid_value` restricts it to the literals and enums a union
 * actually branches on.
 */
function discriminatorOf(branches: readonly (readonly z.core.$ZodIssue[])[]): string | undefined {
    const rejectedBy = new Map<string, number>();
    for (const branch of branches) {
        const named = branch.filter((sub) => sub.code === 'invalid_value');
        for (const path of new Set(named.map((sub) => sub.path.join('.')))) {
            rejectedBy.set(path, (rejectedBy.get(path) ?? 0) + 1);
        }
    }
    let discriminator: string | undefined;
    let mostRejections = branches.length - 2;
    for (const [path, count] of rejectedBy) {
        if (path !== '' && count > mostRejections) {
            discriminator = path;
            mostRejections = count;
        }
    }
    return discriminator;
}

const GENERIC_MESSAGE = 'Invalid input';

/**
 * The issue's message, spelled out from the issue's own data when zod could only
 * produce its locale-less fallback — the model needs the accepted values to retry.
 */
function messageOf(issue: z.core.$ZodIssue): string {
    if (issue.message !== GENERIC_MESSAGE) {
        return issue.message;
    }
    if (issue.code === 'invalid_value') {
        const options = issue.values
            .map((value) => (typeof value === 'string' ? JSON.stringify(value) : String(value)))
            .join('|');
        return `Invalid option: expected one of ${options}`;
    }
    if (issue.code === 'invalid_type') {
        return `${GENERIC_MESSAGE}: expected ${issue.expected}`;
    }
    return issue.message;
}

function describeIssue(issue: z.core.$ZodIssue, parentPath: PropertyKey[]): string[] {
    const path = [...parentPath, ...issue.path];
    if (issue.code !== 'invalid_union') {
        return [`${path.join('.') || 'root'}: ${messageOf(issue)}`];
    }
    // A union failure carries only "Invalid input" — the field that actually went
    // wrong lives in the per-branch sub-issues, whose paths are relative to the
    // union node. Report only the branch the caller was aiming at; listing every
    // branch buries the real complaint under the ones never intended.
    const discriminator = discriminatorOf(issue.errors);
    const intended = discriminator
        ? issue.errors.find((branch) => !branch.some((sub) => sub.path.join('.') === discriminator))
        : undefined;
    if (intended) {
        return intended.flatMap((sub) => describeIssue(sub, path));
    }
    // No branch accepted the value, so the discriminator is itself what is wrong.
    // Report only its own messages: between them they name every value the union
    // takes, and a branch's other faults are fallout from a value it never matched.
    const aboutDiscriminator = issue.errors
        .flat()
        .filter((sub) => sub.path.join('.') === discriminator);
    const reported = aboutDiscriminator.length > 0 ? aboutDiscriminator : issue.errors.flat();
    return reported.flatMap((sub) => describeIssue(sub, path));
}

/**
 * Format Zod validation issues into a single human-readable string for the
 * corrective error message handed back to the model when args fail validation.
 * Union failures are recursed into, so the message names the field to fix rather
 * than just the argument that carried it.
 */
export function formatZodIssues(error: z.ZodError): string {
    return error.issues.flatMap((issue) => describeIssue(issue, [])).join(', ');
}
